import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '../../../context/I18nContext';
import { useModal } from '../../../context/ModalContext';
import { useLayout } from '../../../context/LayoutContext';
import { useLoading } from '../../../context/LoadingContext';
import { useDragResize } from '../../../hooks/useDragResize';
import { useRoomList } from '../../../hooks/useRoomList';
import { useSortedConversations } from '../../../hooks/useSorting';
import { useNavigation } from '../../../context/NavigationContext';
import { useWindowWidth } from '../../../hooks/useWindowWidth';
import useLastViewedRoom from '../../../hooks/useLastViewedRoom';
import useFileAttachment from '../../../hooks/useFileAttachment';
import { useFileDragDrop } from '../../../hooks/useFileDragDrop';
import useMessageQueue from '../../../hooks/useMessageQueue';
import { useAvatar } from '../../../context/AvatarContext';
import logger from '../../../utils/logger';
import SideBar from './SideBar/SideBar';
import NewChatView from './ChatView/NewChatView';
import ChatView from './ChatView/ChatView';
import DragDropOverlay from '../../DragDropOverlay/DragDropOverlay';
import './ConversationsPage.css';

const ConversationsPage = () => {
  const params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useI18n();
  const { showError } = useModal();
  const { sideBarWidth, resizeSideBar, toggleMenu } = useLayout();
  const { showLoading, hideLoading } = useLoading();

  // Track window width to auto-hide sidebar below 700px
  const isWideEnoughForSidebars = useWindowWidth(700);

  const { onMouseDown: onResizeBarMouseDown } = useDragResize({
    onResizeX: resizeSideBar,
    getCurrentX: () => sideBarWidth,
    enableX: true,
    enableY: false,
  });

  const { roomList, isLoading, addRoom } = useRoomList();
  const { navigateToConversation, isCreatingNew, setNavigationGuard } = useNavigation();
  const sortedConversations = useSortedConversations(roomList);
  const [isEditing, setIsEditing] = useState(false);
  const [editFocusTarget, setEditFocusTarget] = useState('topic'); // 'topic' or 'participants'
  const { setLastViewed } = useLastViewedRoom('conversations');
  const { setRoomAvatar } = useAvatar();
  
  // Track if we just created a conversation to prevent auto-redirect
  const justCreatedRoomRef = useRef(null);

  // Extract messageId from query params for deep-linking to specific messages
  const messageIdToFocus = searchParams.get('messageId');

  // Callback to clear the messageId query param after focusing
  const handleMessageFocused = useCallback(() => {
    if (messageIdToFocus) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.delete('messageId');
        return newParams;
      }, { replace: true });
    }
  }, [messageIdToFocus, setSearchParams]);

  const currentId = useMemo(() => {
    if (!params.id) return null;
    // The ID from the URL is already decoded by react-router
    return params.id;
  }, [params.id]);

  // File attachment management
  const {
    attachment,
    isUploading,
    uploadProgress,
    fileInputRef,
    handleStartUpload,
    handleStopUpload,
    handleCancelUpload,
    handleClearAttachment,
    handleFileSelect,
    handleTriggerFileSelect,
    setUploadProgress,
  } = useFileAttachment({
    clearOnRoomChange: true,
    currentRoomId: currentId,
  });
  const { isBusy: isQueueBusy } = useMessageQueue(isCreatingNew ? null : currentId);

  // Validate if the ID from the URL is a real room
  // OR if it's a room we just created (not yet in the synced list)
  const roomExists = currentId && (
    sortedConversations.some(room => room.id === currentId) ||
    justCreatedRoomRef.current === currentId
  );

  // Determine the default room to select if the URL is invalid.
  const defaultId = useMemo(() => {
    // Always select the first (top) conversation in the sorted list
    return sortedConversations[0]?.id || null;
  }, [sortedConversations]);

  // Auto-navigate to default room is current room is invalid and not creating new.
  useEffect(() => {
    // Don't redirect if we just created this room (it might not be in the list yet)
    if (currentId && justCreatedRoomRef.current === currentId) {
      return;
    }

    if (!isCreatingNew && !roomExists && defaultId) {
      navigateToConversation(defaultId);
    }
  }, [isCreatingNew, roomExists, defaultId, currentId, navigateToConversation]);

  // Track the currently viewed conversation
  useEffect(() => {
    if (currentId && roomExists && !isCreatingNew) {
      setLastViewed(currentId);
      // Clear the "just created" flag once the room exists in the list
      if (justCreatedRoomRef.current === currentId) {
        justCreatedRoomRef.current = null;
      }
    }
  }, [currentId, roomExists, isCreatingNew, setLastViewed]);

  // Set up the navigation guard for creating or editing.
  useEffect(() => {
    if (isCreatingNew) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardNewConversationErrorTitle'),
        message: t('ChatView.discardNewConversationErrorDesc'),
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isEditing) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.discardChangesErrorTitle'),
        message: t('ChatView.discardChangesErrorDesc'),
        onConfirm: () => setIsEditing(false), // Reset editing state if user confirms
        confirmText: t('discard'),
        cancelText: t('cancel'),
      });
    } else if (isUploading) {
      setNavigationGuard({
        isActive: true,
        title: t('ChatView.cancelUploadErrorTitle'),
        message: t('ChatView.cancelUploadErrorDesc'),
        onConfirm: handleCancelUpload,
        confirmText: t('yes'),
        cancelText: t('no'),
      });
    } else {
      // Ensure the guard is inactive if not creating or editing.
      setNavigationGuard({ isActive: false });
    }

    // Cleanup function to deactivate the guard when the component unmounts
    return () => setNavigationGuard({ isActive: false });
  }, [isCreatingNew, isEditing, isUploading, setNavigationGuard, handleCancelUpload, t]);

  const handleConversationSelect = useCallback((conversationId) => {
    navigateToConversation(conversationId);
  }, [navigateToConversation]);

  const handleCreateConversation = useCallback(() => {
    navigateToConversation('new');
  }, [navigateToConversation]);

  const handleCreateRoomSave = useCallback(async (roomData, resetCreatingState) => {
    try {
      showLoading();
      
      logger.debug('[ConversationsPage] handleCreateRoomSave - START with roomData:', JSON.stringify({
        title: roomData.title,
        selectedContacts: roomData.selectedContacts,
        initialMessageText: roomData.initialMessageText,
        hasAvatarFile: !!roomData.selectedAvatarFile,
        avatarOption: roomData.selectedAvatarOption,
      }));

      const {
        title,
        selectedContacts,
        initialMessageText,
        selectedAvatarOption,
        selectedAvatarFile,
      } = roomData;

      logger.debug('[ConversationsPage] About to call addRoom with:', {
        title,
        inviteUserIds: selectedContacts.map(c => c.id),
        hasInitialMessage: !!initialMessageText,
        initialMessageText,
      });

      const newRoomId = await addRoom(
        title,
        selectedContacts.map(c => c.id),
        initialMessageText,
      );

      logger.debug('[ConversationsPage] addRoom returned roomId:', newRoomId);

      if (newRoomId) {
        logger.debug('[ConversationsPage] Room created successfully, handling avatar and navigation');

        // Set room avatar if custom or preset was selected
        if (selectedAvatarOption === 'custom' && selectedAvatarFile) {
          logger.debug('[ConversationsPage] Setting custom avatar');
          try {
            await setRoomAvatar(newRoomId, selectedAvatarFile);
          } catch (error) {
            logger.error('[ConversationsPage] Failed to set custom avatar:', error);
            // Provide detailed error message
            const errorMessage = error.message || t('ConversationsPage.avatarUploadFailedMessage');
            showError(
              t('ConversationsPage.avatarUploadFailedTitle'),
              errorMessage,
            );
          }
        } else if (selectedAvatarOption && selectedAvatarOption !== 'default') {
          // Preset avatar - convert path to blob/file
          // Fetch the preset image and upload it
          logger.debug('[ConversationsPage] Setting preset avatar:', selectedAvatarOption);
          try {
            const response = await fetch(selectedAvatarOption);
            if (!response.ok) {
              throw new Error(`Failed to fetch preset avatar: ${response.statusText}`);
            }
            const blob = await response.blob();
            const file = new File([blob], 'avatar.png', { type: blob.type });
            await setRoomAvatar(newRoomId, file);
          } catch (error) {
            logger.error('[ConversationsPage] Failed to set preset avatar:', error);
            const errorMessage = error.message || t('ConversationsPage.avatarUploadFailedMessage');
            showError(
              t('ConversationsPage.avatarUploadFailedTitle'),
              errorMessage,
            );
          }
        }

        // Mark this room as just created to prevent auto-redirect
        justCreatedRoomRef.current = newRoomId;
        logger.debug('[ConversationsPage] Navigating to new room:', newRoomId);
        // Navigate to the new room, bypassing the guard for this successful action.
        navigateToConversation(newRoomId, { bypassGuard: true });
        
        hideLoading();
      } else {
        hideLoading();
        logger.error('[ConversationsPage] ERROR - addRoom returned null/undefined roomId');
        showError(
          t('ConversationsPage.createRoomErrorTitle'),
          t('ConversationsPage.createRoomErrorMessage'),
          resetCreatingState, // Reset on modal close
        );
      }
    } catch (error) {
      hideLoading();
      logger.error('[ConversationsPage] Error in handleCreateRoomSave:', error.message);
      
      // Check if this is a rate limit error
      let errorMessage = t('ConversationsPage.createRoomErrorMessage');
      if (error.httpStatus === 429 || error.errcode === 'M_LIMIT_EXCEEDED') {
        errorMessage = t('ConversationsPage.createRoomRateLimitMessage');
      }
      
      showError(
        t('ConversationsPage.createRoomErrorTitle'),
        errorMessage,
        resetCreatingState, // Reset on modal close
      );
    }
  }, [addRoom, navigateToConversation, setRoomAvatar, showError, showLoading, hideLoading, t]);

  const handleCreateRoomCancel = useCallback(() => {
    navigateToConversation(null);
  }, [navigateToConversation]);

  // Drag & Drop for file attachments
  const handleFilesDropped = useCallback((files) => {
    // Simulate file input change event
    const fakeEvent = {
      target: {
        files: files,
      },
    };
    handleFileSelect(fakeEvent);
  }, [handleFileSelect]);

  const { isDraggingOver, dragHandlers } = useFileDragDrop({
    currentRoomId: currentId,
    isCreatingNew,
    isQueueBusy,
    onFilesDropped: handleFilesDropped,
  });

  const renderSideBar = () => {
    // Hide sidebar when screen width < 700px
    if (!isWideEnoughForSidebars) return null;

    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      left: 0,
      height: '100%',
      width: `${sideBarWidth}px`,
    };

    return (
      <div style={style}>
        <SideBar
          conversations={sortedConversations}
          isCreatingConversation={isCreatingNew}
          selectedConversationId={currentId}
          setCurrentConversationId={handleConversationSelect}
          onCreateConversationClick={handleCreateConversation}
          onMenuToggle={toggleMenu}
          isLoading={isLoading}
        />
      </div>
    );
  };

  const renderResizeBar = () => {
    // Hide resize bar when sidebar is hidden
    if (!isWideEnoughForSidebars) return null;

    const style = {
      position: 'absolute',
      inset: `0px 0px 0px ${sideBarWidth}px`,
      width: '4px',
      zIndex: 1000,
      cursor: 'e-resize',
    };

    return <div style={style} onMouseDown={onResizeBarMouseDown}></div>;
  };

  const renderMainContent = () => {
    const style = {
      display: 'flex',
      position: 'absolute',
      top: 0,
      right: 0,
      height: '100%',
      // Full width when sidebar is hidden by responsive constraint
      width: isWideEnoughForSidebars ? `calc(100% - ${sideBarWidth}px)` : '100%',
    };

    if (isCreatingNew) {
      return (
        <div style={style}>
          <NewChatView onSave={handleCreateRoomSave} onCancel={handleCreateRoomCancel} />
        </div>
      );
    }

    if (!roomExists) {
      return <div style={style} />;
    }

    return (
      <div style={style}>
        <ChatView
          roomId={currentId}
          isEditing={isEditing}
          startEdit={(focusTarget = 'topic') => {
            setEditFocusTarget(focusTarget);
            setIsEditing(true);
          }}
          closeEdit={() => setIsEditing(false)}
          editFocusTarget={editFocusTarget}
          startUpload={handleStartUpload}
          stopUpload={handleStopUpload}
          isUploading={isUploading}
          uploadProgress={uploadProgress}
          setUploadProgress={setUploadProgress}
          onCancelUpload={handleCancelUpload}
          attachment={attachment}
          onTriggerFileSelect={handleTriggerFileSelect}
          onClearAttachment={handleClearAttachment}
          isScreenWideEnoughForDetails={isWideEnoughForSidebars}
          messageIdToFocus={messageIdToFocus}
          onMessageFocused={handleMessageFocused}
        />
      </div>
    );
  };

  return (
    <div 
      className="conversations-page"
      {...dragHandlers}
    >
      { renderSideBar() }
      { renderMainContent() }
      { renderResizeBar() }
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
      />
      {/* Drag & Drop Overlay */}
      <DragDropOverlay 
        text={t('ConversationsPage.dropFileHere')}
        isVisible={isDraggingOver}
      />
    </div>
  );
};

export default ConversationsPage;
